export const checkNumber = (value) => {
  let x = null;
  if (!value.includes(".")) x = Number(value);
  if (
    value.includes(".") &&
    value.split(".")[1] &&
    value.split(".")[1] !== ""
  ) {
    x = Number(value);
  }
  return typeof x === "number" && !isNaN(x);
};

export const updateFilters = (
  filters,
  operator,
  filterName,
  operation,
  value
) => {
  const newFilters = { ...filters };

  // Si la valeur est non nulle et non vide, ou si c'est un tableau (même vide)
  if ((value !== null && value !== "") || Array.isArray(value)) {
    // Si c'est un tableau vide, procéder à la suppression du filtre
    if (Array.isArray(value) && value.length === 0) {
      if (newFilters[operator]) {
        const index = newFilters[operator].findIndex(
          (filter) => Object.keys(filter)[0] === filterName
        );
        if (index !== -1) {
          newFilters[operator].splice(index, 1);
          if (newFilters[operator].length === 0) {
            delete newFilters[operator];
          }
        }
      }
    } else {
      // Sinon, ajouter ou mettre à jour le filtre en préservant les autres opérateurs
      if (!newFilters[operator]) {
        newFilters[operator] = [];
      }

      const existingFilterIndex = newFilters[operator].findIndex(
        (filter) => Object.keys(filter)[0] === filterName
      );

      if (existingFilterIndex !== -1) {
        // Préserver les opérateurs existants et ajouter/mettre à jour le nouveau
        const existingOperations =
          newFilters[operator][existingFilterIndex][filterName] || {};
        newFilters[operator][existingFilterIndex] = {
          [filterName]: {
            ...existingOperations,
            [operation]: value,
          },
        };
      } else {
        // Créer un nouveau filtre avec l'opérateur
        newFilters[operator].push({
          [filterName]: { [operation]: value },
        });
      }
    }
  } else {
    // Logique pour supprimer uniquement l'opérateur spécifique
    if (newFilters[operator]) {
      const filterIndex = newFilters[operator].findIndex(
        (filter) => Object.keys(filter)[0] === filterName
      );

      if (filterIndex !== -1) {
        const currentFilter = newFilters[operator][filterIndex];
        const updatedOperations = { ...currentFilter[filterName] };
        delete updatedOperations[operation];

        // Si plus aucun opérateur, supprimer le filtre complet
        if (Object.keys(updatedOperations).length === 0) {
          newFilters[operator].splice(filterIndex, 1);
          if (newFilters[operator].length === 0) {
            delete newFilters[operator];
          }
        } else {
          // Sinon, mettre à jour avec les opérateurs restants
          newFilters[operator][filterIndex] = {
            [filterName]: updatedOperations,
          };
        }
      }
    }
  }

  return newFilters;
};

export const filtersToQueryString = ({ filters, options = {} }) => {
  if (Object.keys(filters).length === 0) {
    return ""; // Si aucun filtre n'est fourni, retourner une chaîne vide
  }

  let uniqueFilters = {};

  // Fonction auxiliaire pour traiter les valeurs (y compris de manière récursive pour les objets)
  const processValue = (value) => {
    if (value instanceof Date) {
      // Formatage de la date au format ISO avec Z pour indiquer le temps UTC
      return `{"$date":"${value.toISOString()}"}`;
    } else if (Array.isArray(value)) {
      // Gestion des tableaux
      return `[${value.map((v) => processValue(v)).join(",")}]`;
    } else if (typeof value === "object" && value !== null) {
      // Gestion récursive des objets
      let result = {};
      for (const [key, val] of Object.entries(value)) {
        result[key] = processValue(val); // Appel récursif pour chaque propriété de l'objet
      }
      return `{${Object.entries(result)
        .map(([k, v]) => `"${k}":${v}`)
        .join(",")}}`;
    } else {
      // Gestion des autres types de valeurs
      return typeof value === "string" ? `"${value}"` : value;
    }
  };

  Object.entries(filters).forEach(([groupOperator, conditions]) => {
    conditions.forEach((condition) => {
      const [field, conditionObj] = Object.entries(condition)[0];
      const [operator, value] = Object.entries(conditionObj)[0];

      // Définir une clé unique basée sur le champ et l'opérateur
      const uniqueKey = `${field}_${operator}`;

      // Vérifier si la valeur est invalide
      let isInvalidValue =
        value === null ||
        value === "" ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0);

      if (!isInvalidValue) {
        // Ajouter ou mettre à jour le filtre avec la nouvelle valeur valide
        uniqueFilters[uniqueKey] = {
          groupOperator,
          field,
          operator,
          value: processValue(value), // Utilisation de la fonction auxiliaire pour traiter la valeur
        };
      }
    });
  });

  // Suppression des filtres si demandé dans les options
  if (options.removeFilters) {
    options.removeFilters.forEach((removeKey) => {
      Object.keys(uniqueFilters).forEach((key) => {
        if (key.includes(removeKey)) {
          delete uniqueFilters[key];
        }
      });
    });
  }

  // Construction de la chaîne de requête finale
  return Object.values(uniqueFilters)
    .map(({ groupOperator, field, operator, value }) => {
      return `${groupOperator}={"${field}":{"${operator}":${value}}}`;
    })
    .join("&");
};

export const removeFilters = (
  filters,
  groupOperator,
  fieldToRemove,
  operationToRemove
) => {
  // Vérifier si le groupe d'opérateurs spécifié est présent dans filters
  if (filters[groupOperator]) {
    // Parcourir les conditions dans le groupe d'opérateurs
    filters[groupOperator] = filters[groupOperator]
      .map((condition) => {
        // Si la condition concerne le champ à modifier
        if (condition.hasOwnProperty(fieldToRemove)) {
          // Créer une copie de l'objet de condition
          const updatedCondition = { ...condition };
          // Supprimer uniquement l'opération spécifiée
          if (
            updatedCondition[fieldToRemove].hasOwnProperty(operationToRemove)
          ) {
            delete updatedCondition[fieldToRemove][operationToRemove];
          }
          // Si le champ n'a plus d'opérations, supprimer la condition
          if (Object.keys(updatedCondition[fieldToRemove]).length === 0) {
            return null;
          }
          return updatedCondition;
        }
        return condition;
      })
      .filter((condition) => condition !== null);

    // Si le groupe est vide, le supprimer
    if (filters[groupOperator].length === 0) {
      delete filters[groupOperator];
    }
  }
  return filters;
};

export const getOrdoCodes = [
  "AT",
  "BF MED",
  "BF MEDG4",
  "CANL",
  "CADXG6",
  "CADXG6S",
  "ST",
  "STD",
  "STMT",
  "SP BF",
  "SP ST",
  "SP STD",
  "SP AT",
  "BF CAMAPS",
  "BF TANDEM",
  "AT MED",
  "AT YPSO",
  "AT TANDEM",
  "AT BIQ",
  "AT MED740G",
  "BF DIABELOOP",
  "STMT300",
];

export const passwordPattern =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W)[A-Za-z\d\W]{8,}$/;
export const passwordErrorMessage =
  "Le mot de passe doit contenir au moins 8 caractères dont 1 majuscule, 1 minuscule, 1 nombre, 1 symbole.";

export const Locale = {
  sunday: "Di",
  monday: "Lu",
  tuesday: "Ma",
  wednesday: "Me",
  thursday: "Je",
  friday: "Ve",
  saturday: "Sa",
  ok: "OK",
  today: "Aujourd'hui",
  yesterday: "Hier",
  hours: "Heure",
  minutes: "Minutes",
  seconds: "Secondes",
};

export const formatDate = ({ value }) => {
  if (!value || isNaN(new Date(value).getTime()) ) {
    return "--";
  }
  const d = new Date(value);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0"); // Les mois commencent à 0
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatText = ({ value, unit, removeText }) => {
  // Vérifie si value est défini et soit une chaîne soit un nombre
  if (value !== undefined) {
    const stringValue = typeof value === "string" ? value : String(value);
    const formattedValue = stringValue.replace(removeText, "");

    return unit ? `${formattedValue} ${unit}` : formattedValue;
  }

  return "--";
};

/**
 * Détecte si un patient est en contrat LECIGIMON.
 * Priorité : contract.plan.code (contrat actif) → parkinsonType.label (issu de la synchro).
 */
export const isLecigimonPatient = (patient) => {
  const contractCode = patient?.contract?.plan?.code;
  if (contractCode) return ["LEGIC", "LEGICVM", "LECIG", "LECIGVM"].includes(contractCode);
  return patient?.parkinsonType?.label === "LECIGIMON";
};

export const formatObjectText = ({ value, key, unit, removeText }) => {
  // Vérifie si value et value[key] sont définis et soit une chaîne soit un nombre
  if (value && value[key] !== undefined) {
    const stringValue =
      typeof value[key] === "string" ? value[key] : String(value[key]);
    const formattedValue = stringValue
      .replace(new RegExp('"', "g"), "")
      .replace(removeText, "");

    return unit ? `${formattedValue} ${unit}` : formattedValue;
  }

  return "--";
};
